import React, { useCallback, useEffect, useState } from 'react';
import './orbitStar.css';
import { openQuoteChat, scrollPageTop } from '../../../utils/site';
import { renderMarketingIcon } from '../../../utils/marketingIcons';
import MarketingPageLayout from '../../common/layout/MarketingPageLayout';
import FeatureCardsSection from '../../common/marketing/FeatureCardsSection';
import SectionHeader from '../../common/marketing/SectionHeader';
import CtaSection from '../../common/marketing/CtaSection';
import FaqSectionBlock from '../../common/marketing/FaqSectionBlock';
import { getOrbitStarPage } from '../../../data/db';
import OrbitStarSeo from './components/OrbitStarSeo';
import OrbitStarHeroSection from './components/OrbitStarHeroSection';
import OrbitStarVariantsSection from './components/OrbitStarVariantsSection';
import OrbitStarVariantsModal from './components/OrbitStarVariantsModal';
import OrbitStarStickyBuyBar from './components/OrbitStarStickyBuyBar';
import OrbitStarLengthCalculator from './components/OrbitStarLengthCalculator';
import OrbitStarWarrantySection from './components/OrbitStarWarrantySection';

const OrbitStarPage = () => {
    const [variantsModal, setVariantsModal] = useState(null);
    const closeVariants = useCallback(() => setVariantsModal(null), []);
    const openVariants = (source, modelId) => setVariantsModal({ source, modelId });

    useEffect(() => {
        const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
        if (target) {
            target.scrollIntoView();
        } else {
            scrollPageTop();
        }
    }, []);

    const {
        seo,
        hero,
        featuresSection,
        applicationsSection,
        highlightsSection,
        specsSection,
        variantsSection,
        lengthCalculator,
        warrantySection,
        faqSection,
        bulkSection,
        structuredProduct
    } = getOrbitStarPage();

    return (
        <>
            <OrbitStarSeo seo={seo} product={structuredProduct} specs={specsSection.rows} variants={variantsSection} faqItems={faqSection.items} />
            <MarketingPageLayout pageClassName="orbit-star-page">
                <OrbitStarHeroSection hero={hero} onBuy={() => openVariants('hero')} />

                <FeatureCardsSection
                    sectionClassName="orbit-star-features"
                    sectionId="features"
                    header={{
                        wrapperClassName: 'orbit-star-section-header',
                        subtitleClassName: 'orbit-star-section-subtitle',
                        titleClassName: 'orbit-star-section-title',
                        subtitle: featuresSection.subtitle,
                        title: featuresSection.title
                    }}
                    gridClassName="orbit-star-features-grid"
                    cardClassName="orbit-star-feature-card"
                    iconClassName="orbit-star-feature-icon"
                    items={featuresSection.items}
                />

                <OrbitStarVariantsSection section={variantsSection} />

                <OrbitStarLengthCalculator
                    calculator={lengthCalculator}
                    variants={variantsSection}
                    onChoose={(modelId) => openVariants('calculator', modelId)}
                />

                <section id="applications" className="orbit-star-applications">
                    <div className="container">
                        <SectionHeader
                            wrapperClassName="orbit-star-section-header"
                            subtitleClassName="orbit-star-section-subtitle"
                            titleClassName="orbit-star-section-title"
                            subtitle={applicationsSection.subtitle}
                            title={applicationsSection.title}
                        />
                        <ul className="orbit-star-applications-grid">
                            {applicationsSection.items.map((item) => (
                                <li key={item} className="orbit-star-application-card">
                                    <span className="orbit-star-application-icon">{renderMarketingIcon('bulb', 22)}</span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section id="why-orbit-star" className="orbit-star-highlights">
                    <div className="container">
                        <SectionHeader
                            wrapperClassName="orbit-star-section-header"
                            subtitleClassName="orbit-star-section-subtitle"
                            titleClassName="orbit-star-section-title orbit-star-section-title--light"
                            subtitle={highlightsSection.subtitle}
                            title={highlightsSection.title}
                        />
                        <div className="orbit-star-highlights-grid">
                            {highlightsSection.items.map((item) => (
                                <div key={item.label} className="orbit-star-highlight-tile">
                                    <div className="orbit-star-highlight-value">{item.value}</div>
                                    <div className="orbit-star-highlight-label">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="specifications" className="orbit-star-specs">
                    <div className="container">
                        <SectionHeader
                            wrapperClassName="orbit-star-section-header"
                            subtitleClassName="orbit-star-section-subtitle"
                            titleClassName="orbit-star-section-title"
                            subtitle={specsSection.subtitle}
                            title={specsSection.title}
                        />
                        <div className="orbit-star-specs-card">
                            <table className="orbit-star-specs-table">
                                <tbody>
                                    {specsSection.rows.map((row) => (
                                        <tr key={row.label}>
                                            <th scope="row">{row.label}</th>
                                            <td>{row.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                <OrbitStarWarrantySection section={warrantySection} />

                <FaqSectionBlock
                    sectionClassName="orbit-star-faq"
                    sectionId="faq"
                    header={{
                        wrapperClassName: 'orbit-star-section-header',
                        subtitleClassName: 'orbit-star-section-subtitle',
                        titleClassName: 'orbit-star-section-title',
                        subtitle: faqSection.subtitle,
                        title: faqSection.title
                    }}
                    faqProps={{
                        items: faqSection.items,
                        listClassName: 'orbit-star-faq-list',
                        itemClassName: 'orbit-star-faq-item',
                        questionClassName: 'orbit-star-faq-question',
                        iconClassName: 'orbit-star-faq-icon',
                        answerClassName: 'orbit-star-faq-answer'
                    }}
                />

                <CtaSection
                    sectionClassName="orbit-star-bulk"
                    sectionId="bulk-orders"
                    title={bulkSection.title}
                    description={bulkSection.description}
                    buttonClassName="orbit-star-bulk-btn"
                    buttonLabel={bulkSection.buttonLabel}
                    onClick={openQuoteChat}
                />
            </MarketingPageLayout>
            <OrbitStarStickyBuyBar section={variantsSection} onOpen={openVariants} hidden={Boolean(variantsModal)} />
            <OrbitStarVariantsModal
                section={variantsSection}
                isOpen={Boolean(variantsModal)}
                onClose={closeVariants}
                source={variantsModal?.source}
                initialModelId={variantsModal?.modelId}
            />
        </>
    );
};

export default OrbitStarPage;
